import { useEffect, useRef, useState, type ReactElement } from 'react';
import { OrdnerWahl } from '../components/OrdnerWahl';
import { OrdnerSheet } from '../components/OrdnerSheet';
import { OrdnerNeuSheet } from '../components/OrdnerNeuSheet';
import { VerschiebenSheet } from '../components/VerschiebenSheet';
import { ContextMenu, type MenuItem } from '../components/ContextMenu';
import { Sheet } from '../components/Sheet';
import { boxEingerichtet, setzeInstallation } from '../installation';
import { api, type ProjectSummary, type User } from '../api';
import { ChangesSheet, useNewSince } from '../components/ChangesSheet';
import { UsersSheet } from '../components/UsersSheet';
import { AppBar, type AdminScreen, type SelfScreen } from '../components/AppBar';
import { Plan } from './Plan';
import { FeedbackSheet } from '../components/FeedbackSheet';
import { AccountSheet } from '../components/AccountSheet';
import { RightsSheet } from '../components/RightsSheet';
import { BoxSettingsSheet } from '../components/BoxSettingsSheet';
import { AblageSettingsSheet } from '../components/AblageSettingsSheet';
import { BrandKitSheet } from '../components/BrandKitSheet';
import { MailSettingsSheet } from '../components/MailSettingsSheet';
import { KiSettingsSheet } from '../components/KiSettingsSheet';
import { StorageSheet } from '../components/StorageSheet';
import { DiagnoseSheet } from '../components/DiagnoseSheet';
import { AufDenSchirm } from '../components/AufDenSchirm';
import { useTitel } from '../titel';
import { formatWort, imOrdner, ordnerBaum, ordnerName, vorZeit } from '@schnitt/shared';


/** Ab wann ein Druck auf ein Projekt „lang“ ist -- derselbe Wert wie im Schnitt. */
const LANGER_DRUCK = 500;
/**
 * The project list. A project is normally a Box folder -- creating one asks for
 * the folder id rather than inventing a separate place for the material to live
 * -- but it does not have to be one; without a folder the material is uploaded.
 */
const ROLE_WORD: Record<string, string> = { owner: 'Eigentümerin', editor: 'Bearbeiter', viewer: 'Betrachter' };

/**
 * The card's picture: the beginning of the cut, in the shape the project is
 * cut for -- a clapperboard when there is nothing yet or the file will not
 * load.
 */
function Thumb({ url, aspect }: { url: string | null; aspect: number }): ReactElement {
  const [broken, setBroken] = useState(false);
  // Das Bild füllt die Kachel und wird dabei beschnitten; das Format des
  // Projekts steht daneben als Wort. Ein Poster, das seine eigene Form
  // behalten darf, macht aus einem Raster eine Treppe.
  if (!url || broken) {
    return (
      <span className="thumb thumb-empty" aria-hidden="true">
        🎬
      </span>
    );
  }
  return (
    <img
      className="thumb"
      style={{ objectFit: aspect > 1.2 ? 'contain' : 'cover' }}
      src={url}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

export function Projects({
  user,
  onOpen,
  onSignOut,
  onUserChanged,
}: {
  user: User;
  /** `blatt`: gleich mit diesem Blatt auf (7.217: „projekt“). */
  onOpen: (slug: string, blatt?: 'projekt') => void;
  onSignOut: () => void;
  onUserChanged?: (user: User) => void;
}): ReactElement {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** Was gerade gut gegangen ist -- eine Zeile, die von selbst wieder geht. */
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [folderId, setFolderId] = useState('');
  const [format, setFormat] = useState<'vertical' | 'horizontal' | 'square'>('vertical');
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { info, unseen, markSeen } = useNewSince();
  const [showChanges, setShowChanges] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showBox, setShowBox] = useState(false);
  const [zeigeAblage, setZeigeAblage] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const [zeigeKi, setZeigeKi] = useState(false);
  const [showStorage, setShowStorage] = useState(false);
  const [showDiagnose, setShowDiagnose] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showRights, setShowRights] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [query, setQuery] = useState('');
  const [folder, setFolder] = useState('');
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  /** Der Ordner, der gerade umbenannt wird -- und wohin. */
  const [umbenennen, setUmbenennen] = useState<{ von: string; nach: string } | null>(null);
  const [ordnerLaeuft, setOrdnerLaeuft] = useState(false);
  /** Das Blatt „Neuer Ordner“ (7.158). */
  const [neuerOrdner, setNeuerOrdner] = useState(false);
  /** Das Projekt, das gerade verschoben wird -- lang gedrückt (7.158). */
  const [verschieben, setVerschieben] = useState<ProjectSummary | null>(null);
  /**
   * Das Menü eines Projekts (7.217; Sascha, 4. Oktober: „Projekteinstellungen
   * wie Ordner, Name, Sync und Speicher sollten auch im Dashboard ins
   * Kontextmenü“). Bis dahin führte der lange Druck nur zum Verschieben.
   */
  const [projektMenue, setProjektMenue] = useState<{ project: ProjectSummary; x: number; y: number } | null>(null);
  /** Das Projekt, das gerade umbenannt wird, und der Name im Feld. */
  const [projektName, setProjektName] = useState<{ project: ProjectSummary; name: string } | null>(null);
  const [menueLaeuft, setMenueLaeuft] = useState(false);
  /** Die Markensets aller Projekte, aus der Verwaltung (7.219). */
  const [zeigeMarkensets, setZeigeMarkensets] = useState(false);
  /**
   * Lang drücken auf ein Projekt (7.158). Der Zeitgeber läuft ab dem
   * Aufsetzen; wer den Finger bewegt (rollt), hebt ihn auf. Hat er
   * ausgelöst, öffnet der Tipp danach das Projekt *nicht*.
   */
  const druck = useRef<{ timer: number; x: number; y: number; ausgeloest: boolean } | null>(null);
  const druckWeg = () => {
    if (druck.current && !druck.current.ausgeloest) window.clearTimeout(druck.current.timer);
  };
  /**
   * Was im Menü eines Projekts steht (7.217). Name, Ordner, Abgleich und
   * Archiv gehen gleich hier; die übrigen Einstellungen -- der Ordner in der
   * Ablage, Speicher (auslagern, zurückholen), Format -- öffnet „Einstellungen
   * …“ im Projekt, statt sie ein zweites Mal zu bauen.
   */
  const projektMenueEintraege = (project: ProjectSummary): MenuItem[] => {
    const besitzt = project.role === 'owner';
    const darf = project.role !== 'viewer';
    const eintraege: MenuItem[] = [
      { icon: 'play', label: 'Öffnen', gruppe: 'clip', onSelect: () => onOpen(project.slug) },
    ];
    if (darf) {
      eintraege.push({
        icon: 'text',
        label: 'Umbenennen …',
        gruppe: 'clip',
        onSelect: () => setProjektName({ project, name: project.name }),
      });
    }
    eintraege.push({ icon: 'film', label: 'In Ordner verschieben …', gruppe: 'clip', onSelect: () => setVerschieben(project) });
    if (darf && (project.ablage || project.box_folder_id)) {
      eintraege.push({
        icon: 'sync',
        label: 'Mit dem Ordner abgleichen',
        gruppe: 'medium',
        onSelect: async () => {
          try {
            const { added } = await api.syncBox(project.id);
            setHinweis(added > 0 ? `„${project.name}“: ${added === 1 ? 'eine neue Datei' : `${added} neue Dateien`} geholt.` : `„${project.name}“ ist auf dem Stand des Ordners.`);
            await load(showArchived);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Abgleichen ging nicht.');
          }
        },
      });
    }
    if (darf) {
      eintraege.push({
        icon: 'settings',
        label: 'Einstellungen …',
        gruppe: 'medium',
        onSelect: () => onOpen(project.slug, 'projekt'),
      });
    }
    if (besitzt) {
      eintraege.push({
        icon: 'trash',
        label: project.archived ? 'Wiederherstellen' : 'Archivieren',
        gruppe: 'medium',
        danger: !project.archived,
        onSelect: async () => {
          try {
            await api.setProjectArchived(project.id, !project.archived);
            setHinweis(project.archived ? `„${project.name}“ ist wieder da.` : `„${project.name}“ liegt jetzt im Archiv.`);
            await load(showArchived);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Ging nicht.');
          }
        },
      });
    }
    return eintraege;
  };

  /** Mehrere Projekte in einen Ordner -- jedes einzeln, und gesagt, was nicht ging. */
  const inOrdner = async (ids: string[], ordner: string): Promise<{ ok: number; nicht: number }> => {
    let ok = 0;
    let nicht = 0;
    for (const id of ids) {
      try {
        await api.setProjectFolder(id, ordner);
        ok++;
      } catch {
        nicht++;
      }
    }
    await load(showArchived);
    return { ok, nicht };
  };
  // Wie viele Vorschläge auf eine Entscheidung warten. Steht als Zahl am
  // Roadmap-Knopf: sonst liegt dort etwas, und niemand sieht es.
  const [offeneVorschlaege, setOffeneVorschlaege] = useState(0);
  useTitel(showPlan ? null : 'Projekte');
  // Nach dem Anmelden kam die Auskunft über die Installation noch nicht mit
  // (sie hängt an `/api/auth/me`, 7.159) -- einmal nachholen.
  const [, setInstallationDa] = useState(false);
  useEffect(() => {
    void api
      .me()
      .then((r) => {
        setzeInstallation(r.installation);
        setInstallationDa(true);
      })
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    let abgemeldet = false;
    void api
      .roadmap()
      .then((r) => {
        if (!abgemeldet) setOffeneVorschlaege(r.items.filter((i) => i.status === 'vorschlag').length);
      })
      .catch(() => undefined);
    return () => {
      abgemeldet = true;
    };
  }, [showPlan]);

  const openAdmin = (screen: AdminScreen) => {
    if (screen === 'users') setShowUsers(true);
    if (screen === 'box') setShowBox(true);
    if (screen === 'ablage') setZeigeAblage(true);
    if (screen === 'markensets') setZeigeMarkensets(true);
    if (screen === 'mail') setShowMail(true);
    if (screen === 'ki') setZeigeKi(true);
    if (screen === 'storage') setShowStorage(true);
    if (screen === 'diagnose') setShowDiagnose(true);
    if (screen === 'changes') setShowChanges(true);
  };
  const openSelf = (screen: SelfScreen) => {
    if (screen === 'account') setShowAccount(true);
    if (screen === 'rights') setShowRights(true);
    if (screen === 'plan') setShowPlan(true);
    if (screen === 'feedback') setShowFeedback(true);
  };

  // Folders come from the projects themselves; a new one is typed once.
  // Ein Ordner, in dem nur Unterordner liegen, ist trotzdem einer (S47).
  const folders = ordnerBaum(projects).map((k) => k.pfad);
  const needle = query.trim().toLowerCase();
  const shown = needle ? projects.filter((p) => p.name.toLowerCase().includes(needle) || p.folder.toLowerCase().includes(needle)) : projects;
  const baum = ordnerBaum(shown);
  const ohneOrdner = shown.filter((p) => !ordnerName(p.folder));
  const groups: { folder: string; name: string; tiefe: number; gesamt: number; projects: ProjectSummary[] }[] = [
    ...baum.map((k) => ({ folder: k.pfad, name: k.name, tiefe: k.tiefe, gesamt: k.gesamt, projects: k.projekte })),
    ...(ohneOrdner.length ? [{ folder: '', name: '', tiefe: 0, gesamt: ohneOrdner.length, projects: ohneOrdner }] : []),
  ];
  /** Zugeklappt ist, was selbst zu ist -- oder in einem Ordner liegt, der zu ist. */
  const verborgen = (pfad: string) => pfad !== '' && groups.some((g) => g.folder !== pfad && g.folder && openFolders[g.folder] === false && imOrdner(pfad, g.folder));

  /**
   * Welche Liste hier wirklich steht (aus den **geladenen Daten**, nicht aus
   * dem Chip).
   *
   * Der Chip schaltet sofort um, die Liste kommt über die Leitung. Zwischen
   * beidem steht die alte Liste unter der neuen Beschriftung -- und wer von
   * aussen zusieht, kann die beiden nicht unterscheiden. Der Browser-Durchgang
   * hat genau daran zweimal etwas Falsches gemeldet: erst „104 aktiv, 104 im
   * Archiv“ (zweimal dieselbe Liste), und nach der ersten Behebung „105 aktiv,
   * 105 im Archiv“ -- denn auch „steht dieses Projekt darin?“ ist erfüllt,
   * solange die aktive Liste noch dasteht.
   */
  const [geladeneListe, setGeladeneListe] = useState<'aktiv' | 'archiv'>('aktiv');

  const load = async (archived: boolean) => {
    try {
      const { projects } = await api.projects(archived);
      setProjects(projects);
      setGeladeneListe(archived ? 'archiv' : 'aktiv');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Projekte konnten nicht geladen werden.');
    }
  };

  useEffect(() => {
    void load(showArchived);
  }, [showArchived]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const size =
      format === 'vertical'
        ? { width: 1080, height: 1920 }
        : format === 'square'
          ? { width: 1080, height: 1080 }
          : { width: 1920, height: 1080 };
    try {
      const { project } = await api.createProject({
        name: name.trim(),
        boxFolderId: folderId.trim() || undefined,
        ...size,
        // 60 Bilder je Sekunde (7.212; Sascha, 4. Oktober: „Fast immer 60“) --
        // so filmt das iPhone, und ein Export mit 30 wirft jedes zweite weg.
        fps: 60,
        // Neue Projekte fangen magnetisch an (7.201; Sascha, 4. Oktober:
        // „bauen und aktivieren“). In den Projekt-Einstellungen abschaltbar.
        magnetisch: true,
        // Und gleich einen Ordner in der Nextcloud, unter dem Ordner für neue
        // Projekte (7.214) -- ausser es hängt schon an einem Box-Ordner.
        ablageOrdner: !folderId.trim(),
      });
      if (folder.trim()) await api.setProjectFolder(project.id, folder.trim());
      onOpen(project.slug);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Projekt konnte nicht angelegt werden.');
    } finally {
      setBusy(false);
    }
  };

  // Der Fahrplan ist eine eigene Seite, kein Blatt: man liest ihn und sortiert
  // ihn, das braucht Platz.
  if (showPlan) return <Plan user={user} onBack={() => setShowPlan(false)} />;

  return (
    <div className="page start">
      <AppBar
        user={user}
        unseen={unseen}
        version={info?.release ?? null}
        offeneVorschlaege={offeneVorschlaege}
        onAdmin={openAdmin}
        onSelf={openSelf}
        onSignOut={onSignOut}
      />

      {unseen > 0 ? (
        <button
          className="card"
          style={{ width: '100%', textAlign: 'left', marginBottom: 14, cursor: 'pointer' }}
          onClick={() => setShowChanges(true)}
        >
          <div className="spread">
            <div>
              <div className="title">
                {unseen === 1 ? 'Es gibt eine Neuerung' : `Es gibt ${unseen} Neuerungen`}
              </div>
              <div className="sub">{info?.entries[0]?.title}</div>
            </div>
            <span className="badge busy">neu</span>
          </div>
        </button>
      ) : null}

      {error ? <p className="error-text">{error}</p> : null}
      {hinweis ? (
        <p className="hint" data-ordner-hinweis onAnimationEnd={() => setHinweis(null)}>
          {hinweis}
        </p>
      ) : null}

      {showUsers ? <UsersSheet me={user} onClose={() => setShowUsers(false)} /> : null}
      {/* Box liegt in der Ablage, nicht daneben (7.23): „Zurück“ führt dorthin,
          wo man hergekommen ist. */}
      {showBox ? (
        <BoxSettingsSheet
          onClose={() => {
            setShowBox(false);
            setZeigeAblage(true);
          }}
        />
      ) : null}
      {zeigeMarkensets ? <BrandKitSheet verwaltung closeLabel="Fertig" onClose={() => setZeigeMarkensets(false)} /> : null}
      {zeigeAblage ? (
        <AblageSettingsSheet
          onClose={() => setZeigeAblage(false)}
          onBox={() => {
            setZeigeAblage(false);
            setShowBox(true);
          }}
        />
      ) : null}
      {showMail ? <MailSettingsSheet onClose={() => setShowMail(false)} /> : null}
      {zeigeKi ? <KiSettingsSheet onClose={() => setZeigeKi(false)} /> : null}
      {showStorage ? <StorageSheet onClose={() => setShowStorage(false)} /> : null}
      {showDiagnose ? <DiagnoseSheet onClose={() => setShowDiagnose(false)} /> : null}
      {showAccount ? (
        <AccountSheet user={user} onClose={() => setShowAccount(false)} onChanged={(u) => onUserChanged?.(u)} />
      ) : null}
      {showRights ? <RightsSheet user={user} onClose={() => setShowRights(false)} /> : null}
      {showFeedback ? <FeedbackSheet bereich="Startseite" onClose={() => setShowFeedback(false)} /> : null}

      {showChanges ? (
        <ChangesSheet
          info={info}
          istAdmin={user.role === 'admin'}
          onClose={() => {
            markSeen();
            setShowChanges(false);
          }}
        />
      ) : null}

      {/* Der Hinweis auf den Home-Bildschirm steht auf der Startseite und
          nicht im Schnitt: dort arbeitet man, hier kommt man an. */}
      <AufDenSchirm />

      {neuerOrdner ? (
        <OrdnerNeuSheet
          projekte={projects}
          vorhandene={folders}
          laeuft={ordnerLaeuft}
          onClose={() => setNeuerOrdner(false)}
          onFertig={async (name, ids) => {
            setOrdnerLaeuft(true);
            try {
              const { ok, nicht } = await inOrdner(ids, name);
              setNeuerOrdner(false);
              setError(null);
              setHinweis(
                nicht > 0
                  ? `„${name}“: ${ok} verschoben, ${nicht} nicht -- die darfst du nicht ändern.`
                  : `„${name}“ mit ${ok === 1 ? 'einem Projekt' : `${ok} Projekten`}. Einzelne Projekte verschiebst du, indem du lang auf sie drückst.`,
              );
            } finally {
              setOrdnerLaeuft(false);
            }
          }}
        />
      ) : null}
      {projektMenue ? (
        <ContextMenu
          x={projektMenue.x}
          y={projektMenue.y}
          onClose={() => setProjektMenue(null)}
          items={projektMenueEintraege(projektMenue.project)}
        />
      ) : null}
      {projektName ? (
        <Sheet title="Projekt umbenennen" onClose={() => setProjektName(null)}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const name = projektName.name.trim();
              if (!name || name === projektName.project.name) {
                setProjektName(null);
                return;
              }
              setMenueLaeuft(true);
              try {
                await api.renameProject(projektName.project.id, name);
                setProjektName(null);
                setHinweis(`Das Projekt heisst jetzt „${name}“.`);
                await load(showArchived);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Umbenennen ging nicht.');
              } finally {
                setMenueLaeuft(false);
              }
            }}
          >
            <label className="field">
              <span>Name</span>
              <input
                autoFocus
                value={projektName.name}
                maxLength={120}
                data-projekt-name-feld
                onChange={(e) => setProjektName({ ...projektName, name: e.target.value })}
              />
            </label>
            <button className="primary" type="submit" disabled={menueLaeuft || !projektName.name.trim()} data-projekt-name-ok>
              Umbenennen
            </button>
          </form>
        </Sheet>
      ) : null}
      {verschieben ? (
        <VerschiebenSheet
          name={verschieben.name}
          jetzt={verschieben.folder}
          vorhandene={folders}
          laeuft={ordnerLaeuft}
          onClose={() => setVerschieben(null)}
          onFertig={async (ordner) => {
            setOrdnerLaeuft(true);
            try {
              const { ok } = await inOrdner([verschieben.id], ordner);
              setVerschieben(null);
              setError(null);
              if (ok) setHinweis(`„${verschieben.name}“ liegt jetzt ${ordner ? `in „${ordner}“` : 'ohne Ordner'}.`);
              else setError('Dieses Projekt darfst du nicht verschieben.');
            } finally {
              setOrdnerLaeuft(false);
            }
          }}
        />
      ) : null}

      {umbenennen ? (
        <OrdnerSheet
          von={umbenennen.von}
          vorhandene={folders.filter((f) => f !== umbenennen.von)}
          laeuft={ordnerLaeuft}
          onClose={() => setUmbenennen(null)}
          onFertig={async (nach) => {
            setOrdnerLaeuft(true);
            try {
              const res = await api.ordnerUmbenennen(umbenennen.von, nach);
              await load(showArchived);
              setUmbenennen(null);
              const wohin = res.nach ? `„${res.nach}“` : 'ohne Ordner';
              // Dass ein fremdes Projekt stehenblieb, gehört gesagt -- sonst
              // sieht es aus, als hätte das Umbenennen die Hälfte vergessen.
              setError(null);
              setHinweis(
                res.fremd > 0
                  ? `${res.verschoben} verschoben, ${res.fremd} nicht: die gehören jemand anderem.`
                  : `${res.verschoben === 1 ? 'Ein Projekt' : `${res.verschoben} Projekte`} liegen jetzt ${wohin}.`,
              );
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Ging nicht.');
            } finally {
              setOrdnerLaeuft(false);
            }
          }}
        />
      ) : null}

      <div className="start-toolbar">
        <h1>Projekte</h1>
        <div className="chips">
          <button className={`chip ${showArchived ? '' : 'active'}`} onClick={() => setShowArchived(false)}>
            Aktiv
          </button>
          <button className={`chip ${showArchived ? 'active' : ''}`} onClick={() => setShowArchived(true)}>
            Archiv
          </button>
        </div>
        <input
          className="start-search"
          type="search"
          placeholder="Projekt oder Ordner suchen"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Projekte durchsuchen"
        />
        {!showArchived && !creating ? (
          <button className="primary" onClick={() => setCreating(true)}>
            + Neues Projekt
          </button>
        ) : null}
        {/* Ordner gehören auf die Startseite (7.158) -- vorher gab es sie nur
            beim Anlegen eines Projekts und in seinen Einstellungen. */}
        {!showArchived && projects.length > 0 ? (
          <button data-ordner-neu-oeffnen onClick={() => setNeuerOrdner(true)}>
            + Ordner
          </button>
        ) : null}
      </div>

      {projects.length === 0 ? (
        <p className="hint">{showArchived ? 'Nichts archiviert.' : 'Noch kein Projekt. Leg oben eines an.'}</p>
      ) : null}
      {projects.length > 0 && shown.length === 0 ? <p className="hint">Nichts passt zu „{query}“.</p> : null}

      {groups.map((group) => {
        if (verborgen(group.folder)) return null;
        const collapsed = openFolders[group.folder] === false;
        return (
          <section
            className="folder"
            key={group.folder || '(ohne)'}
            data-folder={group.folder || ''}
            data-tiefe={group.tiefe}
            style={{ '--tiefe': group.tiefe } as React.CSSProperties}
          >
            {groups.length > 1 || group.folder ? (
              <div className="folder-head">
                <button
                  className="folder-auf"
                  onClick={() => setOpenFolders({ ...openFolders, [group.folder]: collapsed })}
                  aria-expanded={!collapsed}
                  title={group.folder || undefined}
                >
                  <span className="folder-icon" aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
                  <span className="folder-name">{group.name || 'Ohne Ordner'}</span>
                  <span className="folder-count">{group.gesamt}</span>
                </button>
                {/* „Ohne Ordner“ ist kein Ordner, also gibt es daran auch
                    nichts umzubenennen. */}
                {group.folder ? (
                  <button
                    className="ghost small"
                    data-ordner-menu={group.folder}
                    aria-label={`Ordner „${group.folder}“ ändern`}
                    onClick={() => setUmbenennen({ von: group.folder, nach: group.folder })}
                  >
                    ⋯
                  </button>
                ) : null}
              </div>
            ) : null}
            {collapsed || group.projects.length === 0 ? null : (
              <div className="list project-grid" data-liste={geladeneListe}>
                {group.projects.map((project) => (
                  <button
                    key={project.id}
                    className="project-card"
                    data-projekt-karte={project.id}
                    onPointerDown={(e) => {
                      druckWeg();
                      const eintrag = { timer: 0, x: e.clientX, y: e.clientY, ausgeloest: false };
                      eintrag.timer = window.setTimeout(() => {
                        eintrag.ausgeloest = true;
                        setProjektMenue({ project, x: eintrag.x, y: eintrag.y });
                      }, LANGER_DRUCK);
                      druck.current = eintrag;
                    }}
                    onPointerMove={(e) => {
                      const d = druck.current;
                      if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 8) druckWeg();
                    }}
                    onPointerUp={druckWeg}
                    onPointerCancel={druckWeg}
                    onPointerLeave={druckWeg}
                    onContextMenu={(e) => {
                      // Am Rechner die rechte Taste, am Telefon kommt das
                      // Kontextmenü des Browsers sonst über den langen Druck.
                      e.preventDefault();
                      druckWeg();
                      setProjektMenue({ project, x: e.clientX, y: e.clientY });
                    }}
                    onClick={() => {
                      if (druck.current?.ausgeloest) {
                        druck.current = null;
                        return;
                      }
                      onOpen(project.slug);
                    }}
                    title={`${project.name} — zuletzt ${new Date(project.updated_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}`}
                  >
                    <span className="poster">
                      <Thumb url={project.posterUrl} aspect={project.width / project.height} />
                      <span className="format">{formatWort(project.width, project.height)}</span>
                      {project.role !== 'owner' ? (
                        <span className="rolle">{ROLE_WORD[project.role] ?? project.role}</span>
                      ) : null}
                    </span>
                    <span className="name">{project.name}</span>
                    <span className="meta">
                      {project.asset_count === 1 ? '1 Medium' : `${project.asset_count} Medien`} ·{' '}
                      {vorZeit(project.updated_at)}
                      {/* Ausgelagert (7.51): das Rohmaterial liegt nur noch in
                          der Ablage. Wer das nicht sieht, sucht es auf dem
                          Laufwerk, das er selbst freigegeben hat. */}
                      {project.ausgelagert_am ? (
                        <span className="badge" style={{ marginLeft: 6 }} data-ausgelagert title="Das Rohmaterial liegt nur in der Ablage">
                          ausgelagert
                        </span>
                      ) : null}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        );
      })}

      {creating ? (
        <form className="card" onSubmit={create}>
          {/* Nur, wo es Box gibt (7.159): an einer Installation ohne Box
              fragte das Anlegen hier nach einer Nummer, die nirgends hinführt. */}
          {boxEingerichtet() ? (
          <label className="field">
            <span>Box-Ordner-ID (optional)</span>
            <input
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              placeholder="z. B. 274931845201"
              inputMode="numeric"
            />
            <p className="hint">
              Die lange Zahl aus der Box-Adresse: <code>app.box.com/folder/<b>274931845201</b></code>.
              <br />
              Ohne Ordner lädst du das Material im Projekt vom Gerät hoch.
            </p>
          </label>
          ) : null}
          <label className="field">
            <span>Projektname</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Übernimmt sonst den Ordnernamen" />
          </label>
          <div className="field">
            <span>Ordner</span>
            <OrdnerWahl wert={folder} vorhandene={folders} onWahl={setFolder} />
          </div>
          <label className="field">
            <span>Format</span>
            <select value={format} onChange={(e) => setFormat(e.target.value as typeof format)}>
              <option value="vertical">Hochkant 9:16 — Reels, Stories</option>
              <option value="horizontal">Quer 16:9 — Website, YouTube</option>
              <option value="square">Quadratisch 1:1</option>
            </select>
          </label>
          <div className="stack">
            <button className="primary" type="submit" disabled={busy}>
              {busy ? 'Lege an…' : 'Projekt anlegen'}
            </button>
            <button type="button" className="ghost" onClick={() => setCreating(false)}>
              Abbrechen
            </button>
          </div>
        </form>
      ) : null}

      <p className="hint fuss" style={{ textAlign: 'center', marginTop: 24 }}>
        {info?.release ? (
          <>
            Version: <b data-release>{info.release}</b> ·{' '}
          </>
        ) : null}
        <button className="fuss-link" data-release-notes-link onClick={() => setShowChanges(true)}>
          Release Notes
        </button>
      </p>
    </div>
  );
}
